import { useCallback, useEffect, useState } from "react";
import { PUBLIC_PAGE_GUIDE_OPEN_DELAY_MS } from "@/constants/publicPageGuide";

interface UsePublicPageGuideReturn {
  isGuideOpen: boolean;
  closeGuideForSession: () => void;
  dismissGuidePermanently: () => void;
}

const readGuidePermanentlyDismissed = (storageKey: string): boolean => {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
};

/**
 * First-visit dismissible guide for public pages.
 * Session close (X / overlay) hides until reload; CTA persists in localStorage.
 */
export const usePublicPageGuide = (
  storageKey: string,
  isReady = true,
): UsePublicPageGuideReturn => {
  const [isPermanentlyDismissed, setIsPermanentlyDismissed] = useState(() =>
    readGuidePermanentlyDismissed(storageKey),
  );
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [isViewportReady, setIsViewportReady] = useState(false);

  useEffect(() => {
    if (!isReady) {
      setIsViewportReady(false);
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const frameId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        if (!cancelled) {
          setIsViewportReady(true);
        }
      }, PUBLIC_PAGE_GUIDE_OPEN_DELAY_MS);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    };
  }, [isReady]);

  const isGuideOpen =
    isViewportReady && !isPermanentlyDismissed && !sessionDismissed;

  const closeGuideForSession = useCallback(() => {
    setSessionDismissed(true);
  }, []);

  const dismissGuidePermanently = useCallback(() => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      /* storage unavailable */
    }
    setIsPermanentlyDismissed(true);
    setSessionDismissed(true);
  }, [storageKey]);

  return {
    isGuideOpen,
    closeGuideForSession,
    dismissGuidePermanently,
  };
};
